import { Divider } from "@hortis/ui/divider";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@hortis/ui/hover-card";
import { CheckVerified1 } from "@hortis/ui/icons";
import { IUCNRedListTag } from "@hortis/ui/iucn-red-list-tag";
import { TaxonomicRank } from "generated/graphql";
import { type ReactNode } from "react";
import {
  RecordField,
  RecordFieldLabel,
  RecordFieldValue,
} from "src/features/records/components/record-field/new-record-field";
import { getSharedScientificNameDocument } from "src/features/taxonomy/components/taxon-page/graphql";
import { NameSourceButton } from "src/features/taxonomy/components/taxon-page/taxonomy/source-buttons";
import {
  taxonomicStatusColorMap,
  taxonomicStatusTextColorMap,
} from "src/features/taxonomy/components/taxon-page/taxonomy/taxonomy";
import { useGqlQuery } from "src/utils/hooks/graphql";
import { taxonomicRankLabelMap } from "src/utils/label-maps/taxonomic-rank";
import { taxonomicStatusLabelMap } from "src/utils/label-maps/taxonomic-status";
import { twMerge } from "tailwind-merge";
import { type ScientificNameOption } from "../utils";

export const SharedScientificNameHoverCard = ({
  sharedScientificName,
  children,
}: {
  sharedScientificName: ScientificNameOption;
  children: ReactNode;
}) => {
  const { data } = useGqlQuery({
    query: getSharedScientificNameDocument,
    variables: { id: sharedScientificName.id },
  });
  const lifeForms = data?.sharedScientificName?.taxon?.lifeForms?.nodes.map(
    ({ name }) => name,
  );
  const redListStatus = data?.sharedScientificName?.taxon?.globalRedListStatus;
  return (
    <HoverCard openDelay={300}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        className="flex max-w-[340px] cursor-default flex-col gap-4 text-wrap p-4 pb-5"
        align="start"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <div className="text-sm font-medium text-grey-500">
              {taxonomicRankLabelMap[sharedScientificName.rank]}
            </div>
            <div className="text-sm font-medium text-grey-900">
              {sharedScientificName.scientificName}
            </div>
          </div>
          <NameSourceButton
            className="py-1 shadow-none"
            nameSource={sharedScientificName.nameSource}
            url={data?.sharedScientificName?.nameSourceUrl}
          />
        </div>
        <Divider className="border-dashed" />
        <div className="grid grid-cols-2 gap-4">
          <RecordField>
            <RecordFieldLabel className="h-auto">Authorship</RecordFieldLabel>
            <RecordFieldValue>
              {data?.sharedScientificName?.authorship}
            </RecordFieldValue>
          </RecordField>
          <RecordField>
            <RecordFieldLabel className="h-auto">Status</RecordFieldLabel>
            <RecordFieldValue
              className={twMerge(
                taxonomicStatusTextColorMap[sharedScientificName.status],
                "flex items-center gap-1 font-medium",
              )}
            >
              <CheckVerified1
                size="16px"
                color={taxonomicStatusColorMap[sharedScientificName.status]}
              />
              {taxonomicStatusLabelMap[sharedScientificName.status]}
            </RecordFieldValue>
          </RecordField>
        </div>
        {sharedScientificName.rank !== TaxonomicRank.Genus && (
          <>
            <Divider className="border-dashed" />
            <div className="grid max-w-full grid-cols-2 gap-4">
              <RecordField>
                <RecordFieldLabel className="h-auto">
                  Native distribution
                </RecordFieldLabel>
                <RecordFieldValue>
                  {data?.sharedScientificName?.taxon?.nativeDistribution}
                </RecordFieldValue>
              </RecordField>
              <RecordField>
                <RecordFieldLabel className="h-auto">Climate</RecordFieldLabel>
                <RecordFieldValue>
                  {data?.sharedScientificName?.taxon?.climate}
                </RecordFieldValue>
              </RecordField>
              <RecordField>
                <RecordFieldLabel className="h-auto">
                  IUCN Red List
                </RecordFieldLabel>
                <RecordFieldValue>
                  {redListStatus == null ? null : (
                    <IUCNRedListTag status={redListStatus} />
                  )}
                </RecordFieldValue>
              </RecordField>
              <RecordField>
                <RecordFieldLabel className="h-auto">
                  Life forms
                </RecordFieldLabel>
                <RecordFieldValue>
                  {lifeForms == null || lifeForms.length === 0
                    ? null
                    : lifeForms.join(", ")}
                </RecordFieldValue>
              </RecordField>
            </div>
          </>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};
